
import { Options, Vue, prop } from "vue-class-component";
import ModalShowImage from "@/app/ui/components/modal-show-image/index.vue";
import { UploadMultiple } from "@/domain/entities/MainApp";
import watermark from "watermarkjs";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  resizeImageToDataUrl,
  calculateTextWidth
} from "@/app/infrastructures/misc/common-library/Watermark";
import { ProofPickup } from "@/domain/entities/ShipmentBooking";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  modelValue = prop<any>({
    required: true
  });
  maxSize = prop<number>({
    default: 256,
    type: Number
  });
  multipleIndex = prop<number>({
    default: 0,
    type: Number
  });
  defaultImage = prop<string>({
    default: "",
    type: String
  });
  accept = prop<string>({
    default: "image/png, image/jpg, image/jpeg",
    type: String
  })
  disabled= prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["upload", "remove", "preview", "error"],
  components: {
    ModalShowImage
  },
})
export default class UploadPhotoCodReconcile extends Vue.with(Props) {
  isDragFile = false;
  fileOriginal = "";
  isType = "";

  $refs!: {
    file: HTMLFormElement;
  };

  get isValidPhoto() {
    return this.modelValue && typeof this.modelValue === "string"
  }

  setDragAndDrop(e: any, value: boolean) {
    this.isDragFile = value;
    e.preventDefault();
  }

  get isTypePdf() {
    return this.isType === "pdf";
  }

  get isLoading() {
    return MainAppController.loadingModal;
  }

  onUploadImage(file: any) {
    const type = file.name.split(".").pop().toLowerCase();
    this.isType = type;
    if (this.validateFileType(type)) {
      if (file.size > this.maxSize * 1024 * 1024) this.setError(file);
      else {
        this.fileOriginal = URL.createObjectURL(file);
        this.$emit("update:modelValue", file);
        this.isDragFile = false;
        this.onSetWatermark(file);
      }
    } else {
      this.setDefault();
      this.setError(file);
    }
  }
  
  imageCompression(image: any): any {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(image)
      reader.onload = (event: any) => {
        const img = new Image()
        img.src = event.target.result
        img.onload = (e: any) => {
          const elem = document.createElement('canvas');
          const scaleFactor = 0.65;
          elem.height = scaleFactor * e.target.height
          elem.width = e.target.width * scaleFactor 
          const ctx = elem.getContext('2d')
          ctx?.drawImage(e.target, 0, 0, e.target.width * scaleFactor, e.target.height * scaleFactor)
          ctx?.canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            'image/webp',
            1
          )
        }
      }
    })
  }

  validateFileType(type: string) {
    return ["jpg", "png", "jpeg", "webp"].includes(type);
  }

  onSubmitClick(e: any) {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      this.onUploadImage(file);
    } else this.setDefault();
  }

  onSubmitDrag(e: any) {
    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer && e.dataTransfer.files[0];
      this.onUploadImage(file);
    } else this.setDefault();
  }

  setDefault(e?: any) {
    e?.preventDefault();
    e?.stopPropagation();
    this.$emit("update:modelValue", "");
    this.uploadIntoStorage("");
    this.deleteFromStorage();
    const refValue = this.$refs.file;
    if (refValue) {
      refValue.value = "";
    }
    this.isDragFile = false;
  }

  setError(file: any) {
    this.isDragFile = false;
    this.$emit("error", file);
  }

  uploadIntoStorage(file: any) {
    if (file.size > this.maxSize * 1024 * 1024) return;

    this.$emit("upload", new UploadMultiple({
      index: this.multipleIndex,
      file: file
    }))
  }

  deleteFromStorage() {
    this.$emit("remove", this.multipleIndex)
  }

  onPreview() {
    this.$emit("preview", this.multipleIndex);
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  async onSetWatermark(file: any) {
    MainAppController.showLoading();
    const type = file.type;
    this.fileOriginal = URL.createObjectURL(file);
    if (type.includes("jpeg")) {
      const compressedImage = await this.imageCompression(file);
      this.fileOriginal = URL.createObjectURL(
        new File([compressedImage], file.name, { type: "image/jpeg" })
      );
    }
    await this.$nextTick();

    const img = new Image();
    img.src = this.fileOriginal;

    const logoImg = new Image();
    logoImg.src = require("@/app/ui/assets/svg/logo-lion-parcel.svg");

    // Wait for both images to load
    return Promise.all([
      new Promise((resolve) => {
        img.onload = resolve;
      }),
      new Promise((resolve) => {
        logoImg.onload = resolve;
      })
    ]).then(async () => {
      const smaller = img.width < img.height ? img.width : img.height;

      // Create watermark chain
      const centerFontSize = `${img.width * 0.12}px`;

      let watermarkChain = watermark([this.fileOriginal]).image(
        watermark.text.center(
          "",
          `${centerFontSize} Montserrat extrabold`,
          "#fff",
          0.4
        )
      );

      // Add logo right after the text
      const newWidth = smaller * 0.5;

      const resizedLogoUrl = await resizeImageToDataUrl(logoImg, newWidth);

      // Calculate center position of watermark
      const centerLogoX = img.width / 2 - newWidth / 2;
      const centerLogoY = img.height * 0.6;

      watermarkChain = watermarkChain.load([resizedLogoUrl]).image(
        watermark.image.atPos(
          () => centerLogoX,
          () => centerLogoY,
          1
        )
      );

      const bottomFontSize = `${smaller * 0.035}px`;
      const customText = [
        this.$moment(new Date()).format("dddd, DD MMMM YYYY HH:mm"),
        `${this.$t("codReconcile.dailyDeposit.uploadedBy")} ${this.dataProfile.username}`
      ];
      const font = `${bottomFontSize} Montserrat bold`;

      // Calculate the text width
      const gap = smaller * 0.05;

      customText.forEach((text, index) => {
        const textWidth = calculateTextWidth(text, font);

        // Calculate the center X position of the text
        const centerTextX = img.width / 2 - textWidth / 2;

        watermarkChain = watermarkChain.load([this.fileOriginal]).image(
          watermark.text.atPos(
            () => centerTextX,
            () => img.height - gap * (customText.length - index),
            text,
            font,
            "#ffffff",
            1
          )
        );
      });

      // Process final image
      watermarkChain.then((img: any) => {
        this._onFinalWatermark(img, file);
      });
    });
  }

  _onFinalWatermark(img: any, file: any) {
    fetch(img.src)
      .then((res) => res.blob())
      .then(async (blob) => {
        const fileWatermark = new File([blob], file.name, blob);
        const compressedFileWatermarkBlob = await this.imageCompression(
          fileWatermark
        );
        const compressedFileWatermark = new File(
          [compressedFileWatermarkBlob],
          file.name,
          compressedFileWatermarkBlob
        );
        this.fileOriginal = URL.createObjectURL(compressedFileWatermark);
        MainAppController.closeLoading();
        await this.$nextTick();
        this.uploadIntoStorage(compressedFileWatermark);
      });
  }
}
